import React, { Suspense, lazy } from 'react';
import { ChevronDown } from 'lucide-react';
import { ScrollReveal, TextReveal } from './ScrollAnimations';
import { MagneticButton } from './MagneticElements';
import { ShimmerButton, GlassButton } from './PremiumButtons';

const Spline = lazy(() => import('@splinetool/react-spline'));

const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToExpertise = () => {
    const element = document.querySelector('#expertise');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden"
    >
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" />
      
      {/* Gradient Orbs */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-accent/10 rounded-full blur-[150px] animate-pulse-slow" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-violet-500/10 rounded-full blur-[150px] animate-pulse-slow animation-delay-2000" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen pt-20">
          {/* Left Content */}
          <div className="flex flex-col justify-center z-10">
            <ScrollReveal animation="fadeUp" delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full w-fit mb-8 backdrop-blur-sm">
                <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                <span className="text-white/70 text-sm">Agence Web Premium</span>
              </div>
            </ScrollReveal>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-[1.1] mb-6">
              <TextReveal type="words" staggerDelay={80} duration="slow">
                Nous concevons des sites web
              </TextReveal>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-violet-400">
                <TextReveal type="words" staggerDelay={80} duration="slow">
                  élégants
                </TextReveal>
              </span>
              <TextReveal type="words" staggerDelay={80} duration="slow">
                , performants et sur-mesure.
              </TextReveal>
            </h1>

            <ScrollReveal animation="fadeUp" delay={400}>
              <p className="text-lg md:text-xl text-white/60 max-w-xl mb-10 leading-relaxed">
                Du site vitrine au projet web complexe, nous donnons vie à votre vision digitale avec expertise et créativité.
              </p>
            </ScrollReveal>

            <ScrollReveal animation="fadeUp" delay={600}>
              <div className="flex flex-col sm:flex-row gap-4">
                <MagneticButton
                  as="div"
                  onClick={scrollToContact}
                  strength={0.3}
                >
                  <ShimmerButton onClick={scrollToContact}>
                    Demander un devis
                  </ShimmerButton>
                </MagneticButton>
                
                <MagneticButton
                  as="div"
                  onClick={scrollToExpertise}
                  strength={0.25}
                >
                  <GlassButton onClick={scrollToExpertise}>
                    Découvrir nos services
                  </GlassButton>
                </MagneticButton>
              </div>
            </ScrollReveal>
          </div>

          {/* Right - Spline 3D */}
          <div className="hidden lg:flex items-center justify-center relative">
            <ScrollReveal animation="scaleIn" delay={300} duration="slower">
              <div className="w-[700px] h-[700px] relative overflow-visible">
                <Suspense
                  fallback={
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-16 h-16 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
                    </div>
                  }
                >
                  <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
                </Suspense>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <MagneticButton
        as="div"
        onClick={scrollToExpertise}
        strength={0.5}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer"
      >
        <span className="text-white/40 hover:text-accent transition-colors duration-300 animate-bounce block">
          <ChevronDown size={32} />
        </span>
      </MagneticButton>
    </section>
  );
};

export default HeroSection;
